import React, { useRef } from "react";
import { cn } from "@/lib/utils";

export type TabItem = {
  value: string;
  label: React.ReactNode;
  /** Leading icon (segmented variant tints it brand when active). */
  icon?: React.ReactNode;
  /** Optional count badge after the label. */
  count?: number;
  disabled?: boolean;
};

type Props = {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  /** `underline` = a strip with a bottom-border indicator; `segmented` = a
   *  rounded white track of chips (the Big-5 category strip). */
  variant?: "underline" | "segmented";
  /** Underline colour: `brand` (default) or neutral `ink` (buyer detail drawer). */
  tone?: "brand" | "ink";
  ariaLabel?: string;
  /** Prefix for each tab's id (`${idBase}-${value}`), so a panel elsewhere can
   *  point back with `aria-labelledby`. */
  idBase?: string;
  /** id of the single panel these tabs control (sets `aria-controls`). */
  panelId?: string;
  className?: string;
};

/** One ARIA tablist with roving tabindex + arrow/Home/End keys (automatic
 *  activation — arrowing selects). Three faithful looks via `variant`/`tone`;
 *  supersedes the Big-5 `.vb-tabs`, the drawer `.vb-drawer-tabs`, and the shadcn
 *  tabs on v2 surfaces. */
export function Tabs({
  items,
  value,
  onChange,
  variant = "underline",
  tone = "brand",
  ariaLabel,
  idBase,
  panelId,
  className,
}: Props) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    let target: number | null = null;
    if (e.key === "ArrowRight") target = i + 1;
    else if (e.key === "ArrowLeft") target = i - 1;
    else if (e.key === "Home") target = 0;
    else if (e.key === "End") target = items.length - 1;
    if (target == null) return;
    e.preventDefault();
    // Move in the key's direction, skipping disabled tabs (and wrapping).
    const n = items.length;
    const dir = e.key === "ArrowLeft" || e.key === "End" ? -1 : 1;
    for (let k = 0; k < n; k++) {
      const idx = (((target + dir * k) % n) + n) % n;
      if (!items[idx].disabled) {
        onChange(items[idx].value);
        refs.current[idx]?.focus();
        break;
      }
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "vb-tabs",
        variant === "segmented" ? "vb-tabs--segmented" : "vb-tabs--underline",
        variant === "underline" && tone === "ink" && "vb-tabs--ink",
        className,
      )}
    >
      {items.map((t, i) => {
        const selected = t.value === value;
        return (
          <button
            key={t.value}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="tab"
            id={idBase ? `${idBase}-${t.value}` : undefined}
            aria-selected={selected}
            aria-controls={panelId}
            aria-disabled={t.disabled || undefined}
            disabled={t.disabled}
            tabIndex={selected ? 0 : -1}
            className="vb-tab"
            onClick={() => !t.disabled && onChange(t.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {t.icon != null && (
              <span className="vb-tab__ic" aria-hidden="true">
                {t.icon}
              </span>
            )}
            {t.label}
            {t.count != null && <span className="vb-tab__ct">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
